// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { createBffWebSocket, getSharedBffWebSocket } from '@/services/bffWebSocket';

const DEFAULT_TIMEOUT_MS = 10000;

/**
 * What a person types. The stored token's first six characters — the same shape as the join code
 * the share sheet shows, so a code is a code whichever kind it is.
 */
export const BOARD_CODE_LENGTH = 6;

export type BoardCodeTarget = {
  tourId: string;
  boardToken: string;
  /** The round the code opens — the tour's most-live one. Absent when the tour has no rounds yet. */
  roundId?: string;
};

const createRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `board-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/** Strip the spaces and dashes people type into a code field, and case-fold it. */
export const normalizeBoardCode = (value: string) =>
  value.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase();

/** The short form of a board token, for display. */
export const formatBoardCode = (boardToken: string) =>
  normalizeBoardCode(boardToken).slice(0, BOARD_CODE_LENGTH).toUpperCase();

const awaitBoardReply = (
  socket: ReturnType<typeof createBffWebSocket>,
  requestId: string,
  timeoutMs: number,
  dispose: boolean,
) =>
  new Promise<BoardCodeTarget>((resolve, reject) => {
    let settled = false;

    const finish = (result: { value?: BoardCodeTarget; error?: string }) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      if (dispose) {
        socket.dispose();
      }
      if (result.error || !result.value) {
        reject(new Error(result.error ?? 'missing board payload'));
        return;
      }
      resolve(result.value);
    };

    const timeout = setTimeout(() => finish({ error: 'board lookup timeout' }), timeoutMs);

    const unsubscribe = socket.onMessage((message) => {
      const payload = message as {
        type?: string;
        requestId?: string;
        tourId?: string;
        boardToken?: string;
        roundId?: string | null;
        code?: string;
        message?: string;
      };
      if (!payload.type || payload.requestId !== requestId) {
        return;
      }

      if (payload.type === 'tour.board') {
        const tourId = payload.tourId?.trim();
        const boardToken = payload.boardToken?.trim();
        finish(
          tourId && boardToken
            ? { value: { tourId, boardToken, roundId: payload.roundId?.trim() || undefined } }
            : { error: 'missing board payload' },
        );
        return;
      }

      if (payload.type === 'tour.board.error') {
        finish({ error: payload.code ?? payload.message ?? 'board lookup failed' });
      }
    });
  });

/**
 * "What does this code open?" — answered without a session, which is the point: the person typing
 * it on the start screen has not signed in and is not going to.
 *
 * Uses a throwaway socket rather than the shared one so the lookup cannot disturb (or be disturbed
 * by) whatever the app already has connected. Mirrors resolveTourByInviteCode.
 */
export const resolveBoardCode = async ({
  apiBaseUrl,
  boardToken,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: {
  apiBaseUrl: string;
  boardToken: string;
  timeoutMs?: number;
}): Promise<BoardCodeTarget> => {
  const socket = createBffWebSocket(apiBaseUrl);
  const normalized = normalizeBoardCode(boardToken);
  socket.setAnonymousReadOnly(true);
  socket.setAuthToken(null);

  const requestId = createRequestId();
  const reply = awaitBoardReply(socket, requestId, timeoutMs, true);
  socket.sendTourBoardRequest({ boardToken: normalized, requestId });
  return reply;
};

/**
 * The board code for a tour the caller owns, to put on a screen or read out. Requires a signed-in
 * caller — the BFF refuses this direction anonymously, so a spectator cannot mint a code to pass on.
 */
export const resolveTourBoardCode = async ({
  apiBaseUrl,
  tourId,
  authToken,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: {
  apiBaseUrl: string;
  tourId: string;
  authToken?: string | null;
  timeoutMs?: number;
}): Promise<BoardCodeTarget> => {
  const socket = getSharedBffWebSocket(apiBaseUrl);
  socket.setAuthToken(authToken ?? null);

  const requestId = createRequestId();
  const reply = awaitBoardReply(socket, requestId, timeoutMs, false);
  socket.sendTourBoardRequest({ tourId, requestId });
  return reply;
};
